import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getRequest, postRequest } from "../../services/api/apiService";

const VERTICALS = ["commerce", "apps_and_software", "destinations", "flights", "generic", "hotels", "vehicles"];

const EMPTY_PRODUCT = { id: "", title: "", description: "", link: "", image_link: "", price: "", brand: "" };

// Catalogue produit Meta Commerce Manager — pubs catalogue/dynamiques.
// Comble l'écart "Product catalog" du CLI/MCP officiel Meta (ads-ai-connectors).
const MetaCatalogManager = () => {
  const [catalogs, setCatalogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [newVertical, setNewVertical] = useState("commerce");
  const [creating, setCreating] = useState(false);

  const [selected, setSelected] = useState(null);
  const [products, setProducts] = useState(null);
  const [productsLoading, setProductsLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [adding, setAdding] = useState(false);

  const loadCatalogs = async () => {
    setLoading(true);
    try {
      const res = await getRequest("/api/ads/meta/catalogs");
      setCatalogs(res?.catalogs || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Impossible de charger les catalogues.");
      setCatalogs([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { loadCatalogs(); }, []);

  const createCatalog = async () => {
    if (!newName.trim()) { toast.warn("Donnez un nom au catalogue."); return; }
    setCreating(true);
    try {
      await postRequest("/api/ads/meta/catalogs", { name: newName.trim(), vertical: newVertical });
      toast.success("Catalogue créé.");
      setNewName("");
      loadCatalogs();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Création impossible.");
    } finally { setCreating(false); }
  };

  const openCatalog = async (catalog) => {
    setSelected(catalog);
    setProductsLoading(true);
    try {
      const res = await getRequest(`/api/ads/meta/catalogs/${catalog.id}/products`);
      setProducts(res?.products || []);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Impossible de charger les produits.");
      setProducts([]);
    } finally { setProductsLoading(false); }
  };

  const addProduct = async () => {
    if (!form.id.trim() || !form.title.trim() || !form.link.trim() || !form.price.trim()) {
      toast.warn("ID, titre, lien et prix sont requis (ex. prix : \"9.99 EUR\").");
      return;
    }
    setAdding(true);
    try {
      await postRequest(`/api/ads/meta/catalogs/${selected.id}/products`, { products: [form] });
      toast.success("Produit envoyé au catalogue (traitement asynchrone chez Meta, quelques minutes).");
      setForm(EMPTY_PRODUCT);
      setTimeout(() => openCatalog(selected), 3000);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Ajout impossible.");
    } finally { setAdding(false); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-[#221C51] mb-1">Catalogue produit (Meta)</h1>
      <p className="text-sm text-gray-500 mb-6">
        Gérez vos catalogues Meta Commerce Manager pour les publicités catalogue et dynamiques (rattaché à votre Business Manager).
      </p>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 mb-6">
        <p className="text-xs font-semibold text-gray-500 mb-2">Nouveau catalogue</p>
        <div className="flex flex-wrap gap-2">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Nom du catalogue"
            className="flex-1 min-w-[180px] px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]" />
          <select value={newVertical} onChange={(e) => setNewVertical(e.target.value)}
            className="px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51]">
            {VERTICALS.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <button onClick={createCatalog} disabled={creating}
            className="px-5 py-2 text-sm text-white bg-[#221C51] rounded-lg font-semibold disabled:opacity-50">
            {creating ? "Création…" : "Créer"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-gray-500">Vos catalogues</p>
            <button onClick={loadCatalogs} className="text-xs text-[#221C51] font-semibold">Actualiser</button>
          </div>
          {loading ? <p className="text-sm text-gray-400">Chargement…</p>
            : !catalogs?.length ? <p className="text-sm text-gray-400">Aucun catalogue. Créez-en un ci-dessus.</p>
            : (
              <div className="space-y-2">
                {catalogs.map((c) => (
                  <button key={c.id} onClick={() => openCatalog(c)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg border text-sm ${selected?.id === c.id ? "border-[#221C51] bg-[#221C51]/5" : "border-gray-100 bg-gray-50 hover:border-gray-200"}`}>
                    <span className="font-semibold text-[#221C51]">{c.name}</span>
                    <span className="block text-xs text-gray-400 mt-0.5">{c.vertical} · {c.product_count ?? 0} produit(s)</span>
                  </button>
                ))}
              </div>
            )}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          {!selected ? (
            <p className="text-sm text-gray-400">Sélectionnez un catalogue pour voir/ajouter des produits.</p>
          ) : (
            <>
              <p className="text-xs font-semibold text-gray-500 mb-3">Ajouter un produit — {selected.name}</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="ID (retailer_id) *" className="px-2.5 py-2 text-xs border border-gray-300 rounded-lg" />
                <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Prix (9.99 EUR) *" className="px-2.5 py-2 text-xs border border-gray-300 rounded-lg" />
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre *" className="col-span-2 px-2.5 py-2 text-xs border border-gray-300 rounded-lg" />
                <input value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="Lien produit (https://...) *" className="col-span-2 px-2.5 py-2 text-xs border border-gray-300 rounded-lg" />
                <input value={form.image_link} onChange={(e) => setForm({ ...form, image_link: e.target.value })} placeholder="Lien image" className="col-span-2 px-2.5 py-2 text-xs border border-gray-300 rounded-lg" />
                <input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} placeholder="Marque" className="col-span-2 px-2.5 py-2 text-xs border border-gray-300 rounded-lg" />
              </div>
              <button onClick={addProduct} disabled={adding}
                className="w-full px-4 py-2 text-sm text-white bg-fuchsia-600 rounded-lg font-semibold hover:bg-fuchsia-700 disabled:opacity-50 mb-4">
                {adding ? "Envoi…" : "Ajouter au catalogue"}
              </button>

              <p className="text-xs font-semibold text-gray-500 mb-2">Produits ({products?.length ?? 0})</p>
              {productsLoading ? <p className="text-xs text-gray-400">Chargement…</p>
                : !products?.length ? <p className="text-xs text-gray-400">Aucun produit pour l'instant.</p>
                : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto">
                    {products.map((p) => (
                      <div key={p.id} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-gray-50 text-xs">
                        <span className="text-gray-700 truncate">{p.name || p.retailer_id}</span>
                        <span className="text-gray-400">{p.price}</span>
                      </div>
                    ))}
                  </div>
                )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MetaCatalogManager;
